import { Icons } from "../models/assets-files";
import { Button, Input, InputMap, InputType, SelectOption } from "../models/common.models";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

countries.registerLocale(enLocale);

const countryOptions = Object.entries(countries.getNames("en")).map(([code, name]) => ({
  id: code,
  label: name,
}));

export interface ProductOrService {
  id: string;
  description: string;
  icon: string;
  title: string;
  button: Button;
  inputs: ProductInput | InputMap;
  href: string;
  hasFile: boolean;
}

enum CryptoNetwork {
  Bitcoin = "Bitcoin",
  Ethereum = "Ethereum",
  Litecoin = "Litecoin",
  BitcoinCash = "Bitcoin Cash",
  Dash = "Dash",
  Dogecoin = "Dogecoin",
  EthereumClassic = "Ethereum Classic",
  BinanceSmartChain = "Binance Smart Chain",
  Zcash = "Zcash",
  Tron = "Tron",
  XRP = "XRP",
}

export enum ProductOrServiceId {
  TRACK_MASTER = "track-master",
  WEB_INTELLIGENCE = "web-intelligence",
  BLOCKCHAIN_ANALYSIS = "blockchain-analysis",
  BANK_DISPUTE = "bank-dispute",
  EXCHANGE_DISPUTE = "exchange-dispute",
  CORRESPONDENCE = "correspondence",
}

export enum CorrespondenceId {
  POLICE = "police",
  BANK_DISPUTE = "bank-dispute",
  BANK_DISPUTE_CANADA = "bank-dispute-canada",
  BANK_DISPUTE_GEORGIA = "bank-dispute-georgia",
  ISP = "isp",
  EXCHANGE = "exchange",
  INTERPOL = "interpol",
  CAFC = "cafc",
}

const getProductDescription = (productId: ProductOrServiceId) => {
  switch (productId) {
    case ProductOrServiceId.TRACK_MASTER:
      return "Trackmaster is designed  to provide enhanced cybersecurity protection by precisely identifying potential scammer locations. Through sophisticated analysis and tracking techniques, Trackmaster helps users pinpoint the geographic origin of suspicious activities, enabling proactive measures to safeguard against online threats. This tool empowers individuals and organizations to stay vigilant and take proactive steps to protect their digital assets and sensitive information from malicious actors.";
    case ProductOrServiceId.WEB_INTELLIGENCE:
      return "Web intelligence involves analyzing online data to extract insights. Techniques include web scraping, social media monitoring, and data mining. This data reveals trends, risks, and opportunities, aiding informed decisions and cybersecurity.";
    case ProductOrServiceId.BANK_DISPUTE:
      return `We guide clients in disputing fraudulent bank transactions to reclaim lost funds. Our team compiles evidence and advises on the appropriate course of action with banks, aiming for clients' rights and enhance refund success rates.`;
    case ProductOrServiceId.BLOCKCHAIN_ANALYSIS:
      return `Blockchain Analysis is a vital tool in the fight against cryptocurrency scams, offering crucial insights into the movement and destination of digital assets. By examining the blockchain, we can trace the flow of funds from the victim's wallet to the scammer's address, providing invaluable evidence of fraudulent activity. This process not only helps to identify the perpetrators but also aids in building a case for recovery efforts. Understanding the path of stolen cryptocurrency is essential for victims seeking restitution, as it enables them to pursue legal avenues and engage with relevant authorities. With Blockchain Analysis, victims can gain clarity on the disposition of their funds and take proactive steps towards recovering their assets.`;
    case ProductOrServiceId.CORRESPONDENCE:
      return `Use this section to easily report scammers to their internet or service providers. Fill out the form with key details such as the scammer's identity, the nature of the scam, and any supporting evidence you have. The system will then generate a formal message to be sent directly to the relevant provider, ensuring they take swift action to address the fraudulent activity and protect other users from harm. Secure your online safety by reporting scams promptly and efficiently.`;
    case ProductOrServiceId.EXCHANGE_DISPUTE:
      return `We guide clients in disputing transactions with cryptocurrency exchanges to recover lost funds. Our team compiles evidence and advises on the appropriate course of action with exchanges, aiming to empower clients to navigate the dispute process effectively.`;
  }
};

const paymentMethodOptions: SelectOption[] = [
  { id: "CARD", label: "Card" },
  { id: "CRYPTO", label: "Crypto" },
  { id: "WIRE", label: "Wire" },
  { id: "ELSE", label: "Else" },
];

const communicationChannelsOptions: SelectOption[] = [
  { id: "WHATSAPP", label: "Whatsapp" },
  { id: "PHONE", label: "Phone" },
  { id: "EMAIL", label: "Email" },
  { id: "SMS", label: "Sms" },
  { id: "TELEGRAM", label: "Telegram" },
];

const productInputFields: { [key: string]: Input } = {
  receiptNumber: {
    placeholder: "Receipt Number",
    label: "Receipt Number",
    name: "receiptNumber",
    type: InputType.TEXT,
  },

  bankName: {
    placeholder: "Bank Name",
    label: "Bank Name",
    name: "bankName",
    type: InputType.TEXT,
  },
  name: {
    placeholder: "Name",
    label: "Name",
    name: "name",
    type: InputType.TEXT,
  },

  email: {
    placeholder: "Email",
    label: "Email",
    name: "email",
    type: InputType.TEXT,
  },
  departmentName: {
    optional: true,
    placeholder: "Department Name",
    label: "Department Name",
    name: "departmentName",
    type: InputType.TEXT,
  },

  fraudUrl: {
    type: InputType.TEXT,
    placeholder: "Fraud URL",
    label: "Fraud URL",
    name: "fraudUrl",
  },

  communicationChannel: {
    // type: InputType.TEXT,
    type: InputType.SELECT,
    options: communicationChannelsOptions,
    placeholder: "Comminication Channel",
    label: "Comminication Channel",
    name: "communicationChannel",
  },
  authority: {
    type: InputType.TEXT,
    placeholder: "Authority",
    label: "Authority",
    name: "authority",
  },
  notes: {
    type: InputType.TEXTAREA,
    placeholder: "Notes",
    label: "Notes",
    name: "notes",
  },
  amountLost: {
    name: "amountLost",
    type: InputType.NUMBER,
    placeholder: "Amount Lost",
    label: "Amount Lost",
    step: 1,
    min: 0,
    pattern: "^[0-9]+$",
  },
  transactionDate: {
    name: "transactionDate",
    type: InputType.DATE,
    placeholder: "Transaction Date",
  },
  date: {
    name: "date",
    type: InputType.DATE,
    placeholder: "Date",
    label: "Date",
  },
  transactionsBankUsed: {
    name: "transactionsBankUsed",
    type: InputType.TEXT,
    placeholder: "What bank was used for the transaction",
    label: "What bank was used for the transaction",
  },
  paymentMethod: {
    name: "paymentMethod",
    type: InputType.SELECT,
    options: paymentMethodOptions,
    label: "Payment Method",
  },
  bankStatement: {
    name: "bankStatement",
    type: InputType.FILE,
    label: "Bank Statement",
  },
  websiteName: {
    placeholder: "Website Name",
    label: "Website Name",
    name: "websiteName",
    type: InputType.TEXT,
  },
  websiteUrl: {
    placeholder: "Website URL",
    label: "Website URL",
    name: "websiteUrl",
    type: InputType.TEXT,
  },

  description: {
    placeholder: "Description",
    label: "Description",
    name: "description",
    type: InputType.TEXTAREA,
  },
  relevantInformation: {
    label: "Relevant Information",
    name: "relevantInformation",
    type: InputType.FILE,
  },
  cryptoNetwork: {
    label: "Crypto Network",
    name: "cryptoNetwork",
    type: InputType.SELECT,
    options: [
      { label: CryptoNetwork.Bitcoin, id: CryptoNetwork.Bitcoin },
      { label: CryptoNetwork.Ethereum, id: CryptoNetwork.Ethereum },
      { label: CryptoNetwork.Litecoin, id: CryptoNetwork.Litecoin },
      { label: CryptoNetwork.BitcoinCash, id: CryptoNetwork.BitcoinCash },
      { label: CryptoNetwork.Dash, id: CryptoNetwork.Dash },
      { label: CryptoNetwork.Dogecoin, id: CryptoNetwork.Dogecoin },
      {
        label: CryptoNetwork.EthereumClassic,
        id: CryptoNetwork.EthereumClassic,
      },
      {
        label: CryptoNetwork.BinanceSmartChain,
        id: CryptoNetwork.BinanceSmartChain,
      },
      { label: CryptoNetwork.Zcash, id: CryptoNetwork.Zcash },
      { label: CryptoNetwork.Tron, id: CryptoNetwork.Tron },
      { label: CryptoNetwork.XRP, id: CryptoNetwork.XRP },
    ],
  },
  cryptoWallet: {
    placeholder: "Crypto Wallet",
    label: "Crypto Wallet",
    name: "cryptoWallet",
    type: InputType.TEXT,
  },
  cryptoAmount: {
    placeholder: "Crypto Amount",
    name: "cryptoAmount",
    type: InputType.NUMBER,
  },
  flatAmount: {
    placeholder: "Flat Amount",
    name: "flatAmount",
    type: InputType.NUMBER,
  },
  cryptoTransaction: {
    placeholder: "Crypto Transaction",
    label: "Crypto Transaction",
    name: "cryptoTransaction",
    type: InputType.TEXT,
  },
  transactionsExchangeUsed: {
    placeholder: "Exchange that was used in order to send the funds",
    label: "Exchange that was used in order to send the funds",
    name: "transactionsExchangeUsed",
    type: InputType.TEXT,
  },
  cryptoTransfer: {
    label: "Crypto Transfer",
    name: "cryptoTransfer",
    type: InputType.FILE,
  },
  lawEnforcementAgencyName: {
    optional: true,
    placeholder: "Law Enforcement Agency Name",
    label: "Law Enforcement Agency Name",
    name: "lawEnforcementAgencyName",
    type: InputType.TEXT,
  },

  office: {
    optional: true,
    placeholder: "Office or Unit Address",
    label: "Office or Unit Address",
    name: "office",
    type: InputType.TEXT,
  },
  exchangeService: {
    placeholder: "Exchange Service",
    label: "Exchange Service",
    name: "exchangeService",
    type: InputType.TEXT,
  },
  city: {
    placeholder: "City",
    label: "City",
    name: "city",
    type: InputType.TEXT,
  },
  country: {
    placeholder: "Country",
    label: "Country",
    name: "country",
    type: InputType.SELECT,
    options: countryOptions,
  },

  exchangePlatform: {
    optional: true,
    placeholder: "Exchange Platform",
    label: "Exchange Platform",
    name: "exchangePlatform",
    type: InputType.TEXT,
  },
  walletAddress: {
    optional: true,
    placeholder: "Wallet Address",
    label: "Wallet Address",
    name: "walletAddress",
    type: InputType.TEXT,
  },
  actionsTaken: {
    optional: true,
    placeholder: "Actions Taken",
    label: "Actions Taken",
    name: "actionsTaken",
    type: InputType.TEXT,
  },
  address: {
    placeholder: "Address",
    label: "Address",
    name: "address",
    type: InputType.TEXT,
  },
  bankAddress: {
    placeholder: "Bank Address",
    label: "Bank Address",
    name: "bankAddress",
    type: InputType.TEXT,
  },
  time: {
    placeholder: "Time",
    name: "time",
    type: InputType.TIME,
  },
  price: {
    placeholder: "Price",
    label: "Price",
    name: "price",
    type: InputType.NUMBER,
  },
  additionalFee: {
    placeholder: "Additional Fee",
    label: "Additional Fee",
    name: "additionalFee",
    type: InputType.TEXT,
  },
  crypto: {
    placeholder: "Crypto",
    label: "Crypto",
    name: "crypto",
    type: InputType.NUMBER,
  },
  bitcoinAddress: {
    placeholder: "Bitcoin Address",
    label: "Bitcoin Address",
    name: "bitcoinAddress",
    type: InputType.TEXT,
  },
  rate: {
    placeholder: "Rate",
    label: "Rate",
    name: "rate",
    type: InputType.TEXT,
  },
  txId: {
    placeholder: "Tx Id",
    label: "Tx Id",
    name: "txId",
    type: InputType.TEXT,
  },
  dateOfIncident: {
    placeholder: "Date Of Incident",
    label: "Date Of Incident",
    name: "dateOfIncident",
    type: InputType.DATE,
  },
  ispOrExchange: {
    placeholder: "Isp Or Exchange",
    label: "Isp Or Exchange",
    name: "ispOrExchange",
    type: InputType.TEXT,
  },
  amountInCad: {
    type: InputType.NUMBER,
    placeholder: "Amount in CAD",
    label: "Amount in CAD",
    name: "amountInCad",
  },

  suspectedWalletAddress: {
    placeholder: "Suspected Wallet Address",
    label: "Suspected Wallet Address",
    name: "suspectedWalletAddress",
    type: InputType.TEXT,
  },
  ispName: {
    placeholder: "Isp Name",
    label: "Isp Name",
    name: "ispName",
    type: InputType.TEXT,
  },
  fraudDate: {
    placeholder: "Fraud Date",
    name: "fraudDate",
    type: InputType.DATE,
  },
};

export interface ProductInput {
  inputs: Input[];
}

const getProductInputs = (productId: ProductOrServiceId): ProductInput | InputMap => {
  switch (productId) {
    case ProductOrServiceId.TRACK_MASTER:
      return {
        inputs: [productInputFields.communicationChannel, productInputFields.notes],
      };
    case ProductOrServiceId.BANK_DISPUTE:
      return {
        inputs: [
          productInputFields.amountLost,
          productInputFields.transactionDate,
          productInputFields.transactionsBankUsed,
          productInputFields.paymentMethod,
          productInputFields.bankStatement,
        ],
      };
    case ProductOrServiceId.WEB_INTELLIGENCE:
      return {
        inputs: [
          productInputFields.websiteName,
          productInputFields.websiteUrl,
          productInputFields.description,
          productInputFields.amountLost,
          productInputFields.country,
        ],
      };
    case ProductOrServiceId.BLOCKCHAIN_ANALYSIS:
      return {
        inputs: [
          productInputFields.cryptoNetwork,
          productInputFields.cryptoWallet,
          productInputFields.cryptoTransaction,
          productInputFields.transactionDate,
        ],
      };
    case ProductOrServiceId.EXCHANGE_DISPUTE:
      return {
        inputs: [
          productInputFields.amountLost,
          productInputFields.transactionDate,
          productInputFields.transactionsExchangeUsed,
          productInputFields.paymentMethod,
          productInputFields.cryptoTransfer,
        ],
      };
    case ProductOrServiceId.CORRESPONDENCE:
      return {
        mapSelectLabel: "Correspondence Type",
        inputMap: {
          cafc: {
            inputs: [productInputFields.date, productInputFields.description, productInputFields.amountInCad],
            href: `/${CorrespondenceId.CAFC}`,
          },
          police: {
            inputs: [
              productInputFields.lawEnforcementAgencyName,
              productInputFields.departmentName,
              productInputFields.office,
              productInputFields.city,
              productInputFields.country,
              productInputFields.date,
              productInputFields.amountLost,
              productInputFields.exchangePlatform,
              productInputFields.walletAddress,
              productInputFields.actionsTaken,
              productInputFields.address,
              productInputFields.description,
            ],
            href: `/${CorrespondenceId.POLICE}`,
          },
          bankDispute: {
            inputs: [productInputFields.bankAddress, productInputFields.bankName, productInputFields.amountLost, productInputFields.description],
            href: `/${CorrespondenceId.BANK_DISPUTE}`,
          },
          bankDisputeGeorgia: {
            href: `/${CorrespondenceId.BANK_DISPUTE_GEORGIA}`,
            inputs: [productInputFields.bankAddress, productInputFields.bankName, productInputFields.amountLost, productInputFields.description],
          },
          bankDisputeCanada: {
            href: `/${CorrespondenceId.BANK_DISPUTE_CANADA}`,
            inputs: [
              productInputFields.bankAddress,
              productInputFields.bankName,
              productInputFields.amountLost,
              productInputFields.receiptNumber,
              productInputFields.time,
              productInputFields.price,
              productInputFields.additionalFee,
              productInputFields.crypto,
              productInputFields.bitcoinAddress,
              productInputFields.rate,
              productInputFields.txId,
              productInputFields.description,
            ],
          },
          interpol: {
            href: `/${CorrespondenceId.INTERPOL}`,
            inputs: [
              productInputFields.address,
              productInputFields.actionsTaken,
              productInputFields.amountLost,
              productInputFields.date,
              productInputFields.ispOrExchange,
              productInputFields.country,
              productInputFields.suspectedWalletAddress,
              productInputFields.description,
            ],
          },
          isp: {
            href: `/${CorrespondenceId.ISP}`,
            inputs: [productInputFields.ispName, productInputFields.date, productInputFields.description],
          },
          exchange: {
            href: `/${CorrespondenceId.EXCHANGE}`,
            inputs: [
              productInputFields.exchangeService,
              productInputFields.walletAddress,
              productInputFields.amountLost,
              productInputFields.description,
            ],
          },
        },
      };
  }

  return { inputs: [] };
};

export const productsAndServices: ProductOrService[] = [
  {
    id: ProductOrServiceId.TRACK_MASTER,
    hasFile: false,
    inputs: getProductInputs(ProductOrServiceId.TRACK_MASTER),
    description: getProductDescription(ProductOrServiceId.TRACK_MASTER),
    icon: Icons.ProductsAndServices.TrackMaster,
    title: "Track Master",
    href: `/${ProductOrServiceId.TRACK_MASTER}`,
    button: { cta: ProductOrServiceId.TRACK_MASTER, label: "More Info" },
  },
  {
    id: ProductOrServiceId.WEB_INTELLIGENCE,
    inputs: getProductInputs(ProductOrServiceId.WEB_INTELLIGENCE),
    description: getProductDescription(ProductOrServiceId.WEB_INTELLIGENCE),
    icon: Icons.ProductsAndServices.WebIntelligence,
    hasFile: true,
    title: "Web Intelligence",
    href: `/${ProductOrServiceId.WEB_INTELLIGENCE}`,
    button: { cta: ProductOrServiceId.WEB_INTELLIGENCE, label: "More Info" },
  },
  {
    id: ProductOrServiceId.BLOCKCHAIN_ANALYSIS,
    hasFile: false,
    inputs: getProductInputs(ProductOrServiceId.BLOCKCHAIN_ANALYSIS),
    description: getProductDescription(ProductOrServiceId.BLOCKCHAIN_ANALYSIS),
    icon: Icons.ProductsAndServices.BlockchainAnalysis,
    title: "Blockchain Analysis",
    href: `/${ProductOrServiceId.BLOCKCHAIN_ANALYSIS}`,
    button: { cta: ProductOrServiceId.BLOCKCHAIN_ANALYSIS, label: "More Info" },
  },

  {
    id: ProductOrServiceId.CORRESPONDENCE,
    hasFile: false,
    href: `/${ProductOrServiceId.CORRESPONDENCE}`,
    inputs: getProductInputs(ProductOrServiceId.CORRESPONDENCE),
    description: getProductDescription(ProductOrServiceId.CORRESPONDENCE),
    icon: Icons.ProductsAndServices.Correspondence,
    title: "Correspondence",
    button: { cta: ProductOrServiceId.CORRESPONDENCE, label: "More Info" },
  },
];
